/**
 * TileFooterSlot - Renders controls in the tile-footer location.
 *
 * This slot appears below each individual tile (in the caption area) and is
 * typically used for per-tile controls or metadata like captions.
 */

import React from 'react';

import {type MediaConfigWithDefaults} from '../../Component';
import {CaptionControl} from '../../controls';
import {type ControlDefinition, type GridCell} from '../types';

export type TileFooterSlotProps = {
  controls: ControlDefinition[];
  cell: GridCell;
  config: MediaConfigWithDefaults;
};

/**
 * Renders controls in the tile-footer location.
 *
 * Currently renders captions. Future: can render per-tile controls here.
 *
 * @param props - Tile footer slot props
 * @returns Rendered tile footer or null if no content
 */
export const TileFooterSlot: React.FC<TileFooterSlotProps> = ({
  controls,
  cell,
  config,
}) => {
  if (cell.isEmpty) {
    return null;
  }

  // Caption controls are filtered out when no cells within the row have captions,
  // so we can assume the control should be rendered if it exists.
  // These controls render a minimum height, creating a consistent vertical alignment
  // of media when some tiles within the row have captions and others don't.
  const captionControl = controls.find(
    c => c.type === 'label' && c.control === 'caption'
  );

  // Future: Render per-tile controls from controls array
  const hasOtherControls = controls.some(
    c => c.type !== 'label' || c.control !== 'caption'
  );

  // Only render footer if there's actual content (captions or controls)
  // This prevents empty divs from affecting layout when there are no captions
  if (!captionControl && !hasOtherControls) {
    return null;
  }

  return (
    <div className="tile-footer w-full">
      {captionControl && (
        <CaptionControl
          cell={cell}
          columnIndex={0}
          rowIndex={0}
          control={captionControl}
          index={0}
          config={config}
        />
      )}
      {hasOtherControls && (
        <div className="tile-footer-controls break-words">
          {/* Future: Render per-tile controls here */}
        </div>
      )}
    </div>
  );
};
